import type { ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';

/**
 * Grupo del menú lateral. Dos modos:
 * - Colapsable (default): header <button> con chevron; muestra los items solo cuando `isOpen`.
 * - `alwaysOpen`: header estático (sin toggle ni chevron) y los items SIEMPRE visibles.
 *
 * Componente CONTROLADO y presentacional; el estado del acordeón (si se usa) vive en el AppShell.
 * Estilo pensado sobre el sidebar editorial claro (bg-background / text-foreground).
 */
export function NavGroup({
    title,
    isOpen = false,
    onToggle,
    alwaysOpen = false,
    children,
}: {
    title: string;
    isOpen?: boolean;
    onToggle?: () => void;
    alwaysOpen?: boolean;
    children: ReactNode;
}) {
    const open = alwaysOpen || isOpen;
    return (
        <div>
            {alwaysOpen ? (
                <div className="flex items-center gap-1.5 px-3 py-1.5">
                    <span className="text-xs font-semibold uppercase tracking-wider opacity-60">
                        {title}
                    </span>
                </div>
            ) : (
                <button
                    type="button"
                    onClick={onToggle}
                    aria-expanded={isOpen}
                    className="flex w-full items-center gap-1.5 rounded px-3 py-1.5 text-left transition-colors hover:bg-foreground/5 focus:outline-none focus:ring-1 focus:ring-foreground/30"
                >
                    <span className="text-xs font-semibold uppercase tracking-wider opacity-60">
                        {title}
                    </span>
                    <ChevronDown
                        aria-hidden
                        className={`ml-auto size-4 shrink-0 opacity-60 transition-transform ${
                            isOpen ? 'rotate-180' : ''
                        }`}
                    />
                </button>
            )}
            {open && (
                // Indentación con línea izquierda (pertenencia visual). Solo MARGIN, sin padding extra.
                <div className="mt-0.5 ml-4 border-l border-border">{children}</div>
            )}
        </div>
    );
}
